"use client";

import { memo, useMemo } from "react";

import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { CasinoError } from "@/features/v2/home/components/CasinoSection/components/CasinoError";
import { CasinoSkeleton } from "@/features/v2/home/components/CasinoSection/components/CasinoSkeleton";
import core from "@/features/v2/home/components/localization/core";
import { useCasinoData } from "@/features/v2/shared/hooks/casinoHooks/useCasinoData";
import GameCard from "@/stories/molecules/cards/gameCard/GameCard";

import { CasinoSectionType } from "../enums";
import { CasinoGame } from "../interface";

const getCategorySelected = (type: CasinoSectionType): string => {
  switch (type) {
    case CasinoSectionType.ZINGA:
      return "zing-juegos-en-tendencia";
    case CasinoSectionType.FAVORITES:
      return "zing-tus-favoritos";
    default:
      return "zing-tambien-te-puede-gustar";
  }
};

const CasinoEmpty = memo(({ type }: { type: CasinoSectionType }) => {
  const copy = type === CasinoSectionType.FAVORITES ? core.public.casino.empty : core.public.casino.emptyCatalog;

  return (
    <div className="flex min-h-[164px] md:min-h-[224px] flex-col items-center justify-center gap-2 px-4 text-center" role="status">
      <p className="text-base font-semibold text-white">{copy.title}</p>
      <p className="max-w-md text-sm text-white/70">{copy.description}</p>
    </div>
  );
});

const CasinoGames = memo(
  ({ type = CasinoSectionType.CASINO, instanceId, initialGames }: { type: CasinoSectionType; instanceId?: string; initialGames?: CasinoGame[] }) => {
    const { games, isLoading, error, refetch } = useCasinoData(type, instanceId, initialGames);

    const gameItems = useMemo(() => {
      const categorySelected = getCategorySelected(type);

      return games.map((game, index) => {
        return (
          <CarouselItem key={game.identifier} className="relative basis-[180px] md:basis-[220px] lg:basis-[300px] mr-0 md:mr-2">
            <GameCard
              game={{
                ...game,
                title: game.title ?? "",
                producer: game.producer ?? "",
                provider: game.provider ?? "",
                identifier: String(game.identifier)
              }}
              index={index}
              priority={index < 3}
              categorySelected={categorySelected}
              showFavoriteButton
              showPlayButton
              variant="carousel"
            />
          </CarouselItem>
        );
      });
    }, [games, type]);

    if (error) {
      return <CasinoError error={error} onRetry={refetch} />;
    }

    if (isLoading) {
      return <CasinoSkeleton />;
    }

    if (games.length === 0) {
      return <CasinoEmpty type={type} />;
    }

    return (
      <section aria-label={`Lista de juegos de ${type}${instanceId ? ` - ${instanceId}` : ""}`}>
        <Carousel className="w-full">
          <CarouselContent className="w-full -ml-3 md:-ml-4 pt-2 pr-2">{gameItems}</CarouselContent>
        </Carousel>
      </section>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.type === nextProps.type && prevProps.instanceId === nextProps.instanceId && prevProps.initialGames === nextProps.initialGames;
  }
);

export default CasinoGames;
